import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import {
  getAssetTypes,
  getAssetTypeById,
  getAssetTypesGetStartedOptions,
  saveAssetTypesGetStarted,
  createAssetType,
  updateAssetType,
  updateAssetTypeFieldConfigs,
} from '@/api/asset-type';
import type {
  AssetType,
  AssetTypeKind,
  AssetTypeWithFields,
  SaveAssetTypeGetStartedPayload,
} from '@gokrew/models/asset-type';
import { useAuth } from '@/context/AuthContext';
import { snakeToCamelCase } from '@gokrew/ui/utility/utility';
import { QueryKeys } from '@/constants/query-keys';
import { usePermissions } from '@/context/PermissionContext';
import { permissionKeys } from '@/utility';

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    id
  );

export function useAssetTypes(
  params?: {
    page?: number;
    limit?: number;
    type?: AssetTypeKind;
  },
  options?: { enabled?: boolean }
) {
  const {
    i18n: { language },
  } = useTranslation();
  const { companyId } = useAuth();
  const { permissions } = usePermissions();

  return useQuery({
    queryKey: [QueryKeys.AssetTypes, companyId, params, language],

    queryFn: async () => {
      const res = await getAssetTypes(companyId!, params?.type, language);
      return res.data ?? [];
    },
    enabled:
      (options?.enabled ?? true) &&
      !!companyId &&
      (permissions.has(permissionKeys.asset_types.read) ||
        permissions.has(permissionKeys.asset.read)),
  });
}

export function useAssetTypeGetStartedOptions(options?: { enabled?: boolean }) {
  const { companyId } = useAuth();
  const { permissions } = usePermissions();

  return useQuery({
    queryKey: [QueryKeys.AssetTypesGetStartedOptions, companyId],
    queryFn: async () => {
      const res = await getAssetTypesGetStartedOptions(companyId!);
      return res.data;
    },
    enabled:
      (options?.enabled ?? true) &&
      !!companyId &&
      permissions.has(permissionKeys.asset_types.read),
  });
}

export function useSaveAssetTypeGetStarted(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.AssetType);

  return useMutation({
    mutationFn: (payload: SaveAssetTypeGetStartedPayload) =>
      saveAssetTypesGetStarted(companyId!, payload),

    onSuccess: () => {
      toast.success(t('toast.get_started_saved'));
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AssetTypes] });
      options?.onSuccess?.();
    },

    // The api client's global response interceptor already shows the
    // specific (or generic-fallback) error toast for every failed request —
    // showing a second one here would duplicate it (KH_15).
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useAssetTypeById(id: string) {
  const {
    i18n: { language },
  } = useTranslation();
  const { companyId } = useAuth();
  const { permissions } = usePermissions();

  return useQuery({
    queryKey: [QueryKeys.AssetType, id, language],
    queryFn: async () => {
      const res = await getAssetTypeById(companyId!, id, language);
      return res.data;
    },
    enabled:
      !!companyId &&
      !!isValidUUID(id) &&
      (permissions.has(permissionKeys.asset_types.read) ||
        permissions.has(permissionKeys.asset.read)),
    staleTime: 0,
    select: (data) => ({
      ...data,
      fields:
        data.fields?.map((f) => ({ ...f, formKey: snakeToCamelCase(f.key) })) ??
        [],
    }),
  });
}

export function useCreateAssetType(options?: {
  onSuccess?: (assetType: AssetTypeWithFields) => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.AssetType);

  return useMutation({
    mutationFn: (payload: { name: string }) =>
      createAssetType(companyId!, payload),

    onSuccess: (res) => {
      toast.success(t('toast.created'));
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AssetTypes] });
      options?.onSuccess?.(res.data);
    },

    // See KH_15 note above — the api client interceptor already toasts.
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useUpdateAssetType(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.AssetType);

  return useMutation({
    mutationFn: (payload: AssetType) =>
      updateAssetType(companyId!, payload.id, {
        name: payload.name,
        alias: payload.alias,
      }),

    onSuccess: () => {
      toast.success(t('toast.updated'));
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AssetTypes] });
      options?.onSuccess?.();
    },

    // See KH_15 note above — the api client interceptor already toasts.
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useUpdateAssetTypeConfig(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.AssetType);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        fieldSetId?: string;
        fieldConfigs?: { fieldId: string; isEnabled: boolean }[];
      };
    }) => updateAssetTypeFieldConfigs(companyId!, id, payload),

    onSuccess: (_, variables) => {
      toast.success(t('toast.updated'));
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.AssetType, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AssetTypes] });
      options?.onSuccess?.();
    },

    // See KH_15 note above — the api client interceptor already toasts.
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
